import {
  type MutationKey,
  type QueryKey,
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import type { RequestDocument } from "graphql-request";
import { graphqlRequest } from "@/lib/graphql/client";

export const useGraphqlQuery = <TData, TVariables extends Record<string, unknown> = Record<string, never>>(
  params: {
    queryKey: QueryKey;
    document: RequestDocument;
    variables?: TVariables;
  } & Omit<UseQueryOptions<TData, Error, TData, QueryKey>, "queryKey" | "queryFn">,
) => {
  const { queryKey, document, variables, ...options } = params;

  return useQuery<TData, Error, TData, QueryKey>({
    ...options,
    queryKey,
    queryFn: () => graphqlRequest<TData, TVariables>(document, variables),
  });
};

export const useGraphqlMutation = <TData, TVariables extends Record<string, unknown> = Record<string, never>>(
  params: {
    mutationKey?: MutationKey;
    document: RequestDocument;
  } & Omit<UseMutationOptions<TData, Error, TVariables, unknown>, "mutationFn" | "mutationKey">,
) => {
  const { mutationKey, document, ...options } = params;

  return useMutation<TData, Error, TVariables, unknown>({
    ...options,
    mutationKey,
    mutationFn: (variables) => graphqlRequest<TData, TVariables>(document, variables),
  });
};
